import React, { useState } from 'react';
import * as Icons from '@seismic/icons';
import classnames from 'classnames';
import device from 'current-device';
import { useTranslation } from 'react-i18next';
import UxEventService from '../service/UxEventService';
import { UXContextType, downloadFile, supportDownload, isUrl,
    getFormatIcon, isMobileDevice, getCurrentManifest, canDownloadInDevice,
    isShowSignature, isNoTrackView, supportMultipleDownloads, getDownloadUrl, getContentDownloadAssets } from '../commonUtil/util';
import { isMobileOrTablet } from '../commonUtil/responsiveUtil';
import { withLinkStore, ACTION_TYPES_SHOW_SHARE_MODAL } from '../app/reducers';
import ViewerAvatar from './common/viewerAvatar';
import { ShareIcon, CaretDownIcon } from './common/icons';
import { isPresentationMode } from '../commonUtil/presentationUtil';
import { DOWNLOAD_MODE, KeyBoardAction } from '../commonUtil/constants';
import FormatsDropdown from './common/formatsDropdown';

const DownloadLabel = (props) => {
    const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
    const { t } = useTranslation();
    const { highlightColor, downloadUrl, downloadUrls, contentId, downloadFormats, fileFormat, enableLiveSendDownloadOptions } = props;
    const isMultiDownload = enableLiveSendDownloadOptions && downloadFormats === DOWNLOAD_MODE.PDFOrOriginal
        && downloadUrls.length > 1 && supportMultipleDownloads(fileFormat);

    const onDownload = (item) => {
        setShowDownloadDropdown(false);
        if (item && item.downloadUrl) {
            downloadFile({ downloadUrl: item.downloadUrl, downloadFormat: item.assetType, contentId });
            UxEventService.navigateViewEvent({ Context: UXContextType.HEADER, Target: 'Download' });
        }
    };

    const downloadHandler = () => {
        if (isMultiDownload) {
            setShowDownloadDropdown(!showDownloadDropdown);
            return;
        }

        const item = getDownloadUrl(fileFormat, downloadFormats, downloadUrls);
        if (item && item.downloadUrl) {
            onDownload(item);
        } else if (downloadUrl) {
            // TODO: remove get download url form manifest after the download option is released
            onDownload({ downloadUrl });
        }
    };

    const handleOnKeydown = (e) => {
        if (e.keyCode === KeyBoardAction.enter) {
            downloadHandler();
        }
    };

    const btnColor = isMobileDevice.any() ? '#536872' : highlightColor;
    const btnWrapperClass = classnames('download-icon-wrapper', {
        'with-dropdown': isMultiDownload
    });

    let downloadIcon;
    if (!isMultiDownload) {
        downloadIcon = null;
    } else if (isMobileDevice.any() && isMobileOrTablet()) {
        downloadIcon = <span className="more-action-icon" style={{ borderLeftColor: highlightColor }}><Icons.MoreIcon color={btnColor} /></span>;
    } else if (!isMobileDevice.any() || !isMobileOrTablet()) {
        downloadIcon = <span className="more-action-icon" style={!isMobileDevice.any() ? { borderLeftColor: highlightColor } : {}}><CaretDownIcon color={btnColor} /></span>;
    }

    return (
        <div className={btnWrapperClass}
            onClick={downloadHandler}
            onKeyDown={handleOnKeydown}
            role="button"
            tabIndex="0"
            style={{ color: btnColor, borderColor: btnColor }}>
            {!isMultiDownload && <Icons.DownloadIcon style={{ color: btnColor }} />}
            <span className="text" style={{ color: btnColor }}>{t('LIVE_SEND_VIEWER_DOWNLOAD')}</span>
            {downloadIcon}
            {showDownloadDropdown && <FormatsDropdown
                className="header-download-dropdown"
                onClick={onDownload}
                downloadUrls={downloadUrls}
                originalFormat={fileFormat}
                onClose={() => { setShowDownloadDropdown(false); }} />}
        </div>
    );
};

const OpenLinkButton = (props) => {
    const { t } = useTranslation();
    const { highlightColor, linkUrl } = props;
    const openLinkHandler = () => {
        window.open(linkUrl, '_blank');
        UxEventService.navigateViewEvent({ Context: UXContextType.HEADER, Target: 'Open Link' });
    };

    const handleOnKeydown = (e) => {
        if (e.keyCode === KeyBoardAction.enter) {
            openLinkHandler();
        }
    };

    return (
        <div className="open-icon-container"
            onClick={openLinkHandler}
            onKeyDown={handleOnKeydown}
            role="button"
            tabIndex="0"
            style={{ color: highlightColor, borderColor: highlightColor }}>
            <Icons.OpenIcon style={{ color: highlightColor }} />
            <span className="text" style={{ color: highlightColor }}> {t('LIVE_SEND_VIEWER_OPEN_LINK_BUTTON')}</span>
        </div>
    );
};

const ShareButton = (props) => {
    const { t } = useTranslation();
    const { highlightColor, openShareModal } = props;

    const handleOnKeydown = (e) => {
        if (e.keyCode === KeyBoardAction.enter) {
            openShareModal();
        }
    };

    return (
        <div className="share-icon-wrapper"
            onClick={openShareModal}
            onKeyDown={handleOnKeydown}
            role="button"
            tabIndex="0"
            style={{ backgroundColor: highlightColor, borderColor: highlightColor }}>
            <ShareIcon />
            <span className="text">{t('LIVE_SEND_VIEWER_SHARE')}</span>
        </div>
    );
};

const Header = (props) => {
    const { t } = useTranslation();
    const { openPanel, setOpenPanel, linkStore } = props;

    const { contents, sender, viewerSetting, linkBasicInfo, selectedContent, selectedManifest, creatingSession, isOnlyFunctionalCookies, downloadAssets, isStrictlyNecessaryCookies } = linkStore;
    const { logo, brandingHilightColor } = viewerSetting;
    const { canDownload, isAllowSharingLink, isExpired, interactionId, downloadFormats, enableLiveSendDownloadOptions } = linkBasicInfo;
    const currentManifest = getCurrentManifest(selectedManifest);
    const downloadUrls = getContentDownloadAssets(downloadAssets, selectedContent);
    const hasDownloadUrl = enableLiveSendDownloadOptions ? downloadUrls.length > 0 : currentManifest && currentManifest.DownloadUrl;
    const contentCount = contents.length;
    const showDownload = canDownload && supportDownload(selectedContent.format) && hasDownloadUrl && canDownloadInDevice();

    const mailtoHandler = () => {
        UxEventService.navigateViewEvent({ Context: UXContextType.HEADER, Target: 'Send Email' });
    };

    const togglePanel = () => {
        setOpenPanel(!openPanel);
    };

    const getDisplayOtherfiles = (fileCount) => {
        if (fileCount > 2) {
            return t('LIVE_SEND_VIEWER_WITH_NUMBER_OTHER_FILES', { replace: { number: fileCount - 1 } });
        } else if (fileCount === 2) {
            return t('LIVE_SEND_VIEWER_WITH_NUMBER_OTHER_FILE');
        }

        return '';
    };

    const openShareModal = () => {
        props.linkDispatch({ type: ACTION_TYPES_SHOW_SHARE_MODAL, payload: true });
        UxEventService.navigateViewEvent({ Context: UXContextType.HEADER, Target: 'Show Share Modal' });
    };

    const headerClassname = classnames('d-flex justify-content-between seismic-engagement-viewer-header', { 'mobile-device': device.mobile() });

    const showShareButton = interactionId && isAllowSharingLink && !creatingSession && !isExpired && !isMobileDevice.any()
        && !isNoTrackView() && !isOnlyFunctionalCookies && !isStrictlyNecessaryCookies;

    return (
        <div role="banner" aria-label="Header" className={headerClassname}>
            <div className="d-flex seismic-engagement-viewer-header-left">
                {
                    contentCount > 1 && isMobileDevice.any() ?
                        null :
                        <div className={`logo-wrapper ${contentCount === 1 ? 'display' : ''}`}>
                            {logo && <img src={logo} alt="logo" />}
                        </div>
                }
                {
                    isMobileDevice.any() && contentCount > 0 &&
                    <div className="seismic-engagement-viewer-header-menu-bar" onClick={togglePanel}>
                        <Icons.CaretLeftIcon />
                    </div>
                }
                <img className="format-icon"
                    alt={`${selectedContent.format} File Type`}
                    src={getFormatIcon(selectedContent.format, currentManifest.Url)} />
                <div className="summary">
                    <div className="file-name text-truncate-base">{selectedContent.name}</div>
                    {(isShowSignature() && ((!openPanel || contentCount === 1) && sender.name)) && <div className="sender-name">
                        {t('LIVE_SEND_VIEWER_SHARED_BY')}
                        {!sender.isAnonymousSender && <ViewerAvatar className="sender-avatar" sender={sender} brandingHilightColor={viewerSetting.brandingHilightColor} />}
                        {!sender.isAnonymousSender && <a onClick={mailtoHandler} href={`mailto:${sender.email}`} className="email-to-sender">{sender.name}</a>}
                        {sender.isAnonymousSender && <span>{sender.name}</span>}
                        {getDisplayOtherfiles(contentCount)}
                    </div>}
                </div>
            </div>
            {!isPresentationMode() && <div className={`d-flex seismic-engagement-viewer-header-right ${contentCount === 1 ? 'single-doc' : 'multi-doc'}`}>
                {showDownload && <DownloadLabel highlightColor={brandingHilightColor}
                    downloadUrl={currentManifest.DownloadUrl}
                    downloadUrls={downloadUrls}
                    fileName={selectedContent.name}
                    fileFormat={selectedContent.format}
                    contentId={selectedContent.id}
                    contentVersionId={selectedContent.sourceContentVersionId}
                    downloadFormats={downloadFormats}
                    enableLiveSendDownloadOptions={enableLiveSendDownloadOptions} />}
                {isUrl(selectedContent.format) && currentManifest.Url &&
                    <OpenLinkButton highlightColor={brandingHilightColor}
                        linkUrl={currentManifest.Url} />}
                {/* the share button in web */}
                {showShareButton
                    && <ShareButton highlightColor={brandingHilightColor} openShareModal={openShareModal} />}
            </div>}
        </div>
    );
};

export default withLinkStore(Header);
